import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import { RiskBandsAdvancedSettings, RiskBandsAdvancedSettingsRefs } from "@/components/layouts/RiskBandsAdvancedSettings";
import { getConfig } from "@/features/branding";
import { GeocodingHelpButton } from "@/features/geocoders";
import { useGetToolUsageQuery } from "@/store/services/supabase";
import ScreeningFairUseWarning from "@/tools/risk-bands/components/ScreeningFairUseWarning";
import { ScreeningLimitReview, ScreeningReviewModalRefs } from "@/tools/risk-bands/components/ScreeningLimitReview";
import { RiskBandsAdvancedSettingsValues } from "@/tools/risk-bands/types";
import { formatSettingSwitchValue } from "@/utils/string";
import { Alert, Button, Col, Divider, Input, Row, Typography } from "antd";
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
const {
  TextArea
} = Input;
const {
  Text,
  Title
} = Typography;
export interface RiskBandsBatchStep1Refs {
  addresses: MutableRefObject<null>;
  process: MutableRefObject<null>;
  review: MutableRefObject<null>;
  advancedSettings?: RiskBandsAdvancedSettingsRefs;
  reviewModal?: ScreeningReviewModalRefs;
}
interface RiskBandsBatchStep1Props {
  addresses: string;
  setAddresses: (addresses: string) => void;
  onResetSettings: () => void;
  onProgress: (addresses: string[]) => void;
  isReviewModalTutorialOpen: boolean;
  isAdvancedOpen: boolean;
  advancedSettings: any;
  setAdvancedSettings: Dispatch<SetStateAction<RiskBandsAdvancedSettingsValues>>;
  setRefs: (refs: RiskBandsBatchStep1Refs) => void;
}
export const RiskBandsBatchStep1 = ({
  addresses,
  setAddresses,
  onResetSettings,
  onProgress,
  isReviewModalTutorialOpen,
  isAdvancedOpen,
  advancedSettings,
  setAdvancedSettings,
  setRefs
}: RiskBandsBatchStep1Props) => {
  const {
    maxAddresses
  } = getConfig().riskBands.quickBatch;
  const addressesRef = useRef(null);
  const processRef = useRef(null);
  const reviewButtonRef = useRef(null);
  const [settingsRefs, setSettingsRefs] = useState<RiskBandsAdvancedSettingsRefs | undefined>(undefined);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewModalRefs, setReviewModalRefs] = useState<ScreeningReviewModalRefs | undefined>(undefined);
  const [addressCount, setAddressCount] = useState(0);
  const {
    t
  } = useTranslation();
  const {
    t: wt
  } = useTranslation("widgets", {
    keyPrefix: "textInput.addresses"
  });
  const [showUnspecificAddressMessage, setShowUnspecificAddressMessage] = useState<boolean>(false);
  const splitAddresses = (input: string): string[] => {
    return input.split("\n").map(address => address.trim()).filter(address => address !== "");
  };
  const handleProgress = () => {
    onProgress(splitAddresses(addresses));
  };
  useEffect(() => {
    setAddressCount(splitAddresses(addresses).length);
  }, [addresses]);
  useEffect(() => {
    setRefs({
      addresses: addressesRef,
      advancedSettings: settingsRefs,
      process: processRef,
      review: reviewButtonRef,
      reviewModal: reviewModalRefs
    });
  }, [addressesRef, processRef, settingsRefs, reviewButtonRef]);
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  if (!toolUsage) {
    return <></>;
  }
  const currentUsage = toolUsage.singleQuotaMode ? toolUsage.singleQuotaUsage : toolUsage.screening;
  const validateAddresses = () => {
    const addressList = splitAddresses(addresses);
    const unspecificAddress = addressList.filter(address => {
      // is coordinate
      const isCoordinate = /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/.test(address);
      if (isCoordinate) return false;
      const containsTwoWords = address.split(/\s+/).length >= 3;
      const containsNumber = /\d/.test(address);
      const containsComma = /^(?!,)(.*,.+)(?<!,)$/g.test(address);
      // is specific
      if (containsTwoWords && containsNumber && containsComma) return false;

      // is unspecific
      return true;
    });
    setShowUnspecificAddressMessage(!!unspecificAddress.length);
  };
  return <Row style={{
    width: "100%",
    gap: "24px"
  }} data-sentry-element="Row" data-sentry-component="RiskBandsBatchStep1" data-sentry-source-file="BatchStep1.tsx">
      <Col flex="1 1" data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
        <Row ref={addressesRef} data-sentry-element="Row" data-sentry-source-file="BatchStep1.tsx">
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <Row align="middle" className="mb-2 gap-1" data-sentry-element="Row" data-sentry-source-file="BatchStep1.tsx">
              <Title level={5} className="m-0 !mb-0 p-0" data-sentry-element="Title" data-sentry-source-file="BatchStep1.tsx">
                Enter Addresses <ContextualInfoButton id="enteringLocationsAndAddresses" data-sentry-element="ContextualInfoButton" data-sentry-source-file="BatchStep1.tsx" />
              </Title>
              <GeocodingHelpButton data-sentry-element="GeocodingHelpButton" data-sentry-source-file="BatchStep1.tsx">Geocoding Help</GeocodingHelpButton>
            </Row>

            <TextArea aria-label="Addresses" placeholder={wt("placeholder", {
            count: maxAddresses
          }) as string} onChange={event => setAddresses(event.target.value)} rows={20} style={{
            resize: "none"
          }} value={addresses} data-sentry-element="TextArea" data-sentry-source-file="BatchStep1.tsx" />
          </Col>
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <Text aria-label="Addresses Status" type={addressCount > maxAddresses ? "danger" : "secondary"} data-sentry-element="Text" data-sentry-source-file="BatchStep1.tsx">
              {wt(addressCount > maxAddresses ? "countOverLimit" : "countWithinLimit", {
              count: addressCount,
              max: maxAddresses
            })}
            </Text>
            <Alert rootClassName="text-sky-700 border-none mt-2" message={<div>
                  Unspecific addresses may result in incorrect geocoding. Please enter coordinates or street level
                  addresses. Avoid business names or landmarks. For more information, read the{" "}
                  <GeocodingHelpButton className="!text-sm !text-sky-700" isLink>
                    geocoding guide
                  </GeocodingHelpButton>{" "}
                  here.
                </div>} data-sentry-element="Alert" data-sentry-source-file="BatchStep1.tsx" />
          </Col>
        </Row>
      </Col>
      <Col flex={0} style={{
      width: "100%",
      maxWidth: "320px"
    }} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
        <Row data-sentry-element="Row" data-sentry-source-file="BatchStep1.tsx">
          <Col span={24} style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 0,
          marginBottom: 2
        }} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <Title level={5} style={{
            paddingTop: 0,
            margin: 0
          }} data-sentry-element="Title" data-sentry-source-file="BatchStep1.tsx">
              Settings
            </Title>
            <Button type="text" onClick={onResetSettings} size="small" data-sentry-element="Button" data-sentry-source-file="BatchStep1.tsx">
              Reset
            </Button>
          </Col>
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <RiskBandsAdvancedSettings isAdvancedOpen={isAdvancedOpen} setRefs={setSettingsRefs} notice="Quick Batch applies the same settings to all supplied addresses." values={advancedSettings} setValues={setAdvancedSettings} data-sentry-element="RiskBandsAdvancedSettings" data-sentry-source-file="BatchStep1.tsx" />
          </Col>
          <Divider style={{
          marginTop: 12,
          marginBottom: 12
        }} data-sentry-element="Divider" data-sentry-source-file="BatchStep1.tsx" />
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            {currentUsage && <>
                <ScreeningFairUseWarning toolUsage={toolUsage} currentUsage={currentUsage} hideDetail />
                <ScreeningLimitReview setRefs={setReviewModalRefs} isOpen={isReviewModalOpen || isReviewModalTutorialOpen} title="Quick Batch Screening" uploadedAssetCount={addressCount} continueText={t("actions:process")} onBack={() => setIsReviewModalOpen(false)} onContinue={() => {
              setIsReviewModalOpen(false);
              handleProgress();
            }} basicSettings={[{
              label: t("terms:address"),
              value: <div className="flex flex-col gap-1">
                          <TextArea rows={addressCount > 1 ? 3 : 1} readOnly value={addresses} style={{
                  resize: "none"
                }} />
                          {showUnspecificAddressMessage && <Alert rootClassName="text-sky-700 border-none mt-2 text-xs" message={<div>
                                  Some addresses you entered may not specific enough. This may result in incorrect
                                  geocoding. Please enter coordinates or street level addresses. Avoid business names or
                                  landmarks. For more information, read the{" "}
                                  <GeocodingHelpButton className="!text-xs !text-sky-700" isLink>
                                    geocoding guide
                                  </GeocodingHelpButton>{" "}
                                  here.
                                </div>} />}
                        </div>
            }, {
              label: t("terms:archetype"),
              value: advancedSettings.archetype
            }, {
              label: t("terms:buildYear"),
              value: advancedSettings.buildYear.amount
            }, {
              label: t("terms:heightAboveGround"),
              value: advancedSettings.heightAboveGround.amount
            }, {
              label: "Total assets",
              value: addressCount
            }]} advancedSettings={[{
              label: t("tools:settings.anomalyManagementRp.title"),
              value: formatSettingSwitchValue(advancedSettings.anomalyManagementRp)
            }, {
              label: t("tools:settings.defences.title"),
              value: formatSettingSwitchValue(advancedSettings.defences)
            }]} />
              </>}
            <Button ref={reviewButtonRef} block type="primary" disabled={addressCount === 0 || addressCount > maxAddresses} onClick={() => {
            validateAddresses();
            setIsReviewModalOpen(true);
          }} data-sentry-element="Button" data-sentry-source-file="BatchStep1.tsx">
              Review
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>;
};